import { CameraStatusEnum, PermissionEnum } from '@api/enum';
import { ICameraCCTV } from '@api/models/camera-cctv.model';
import useDialog from '@hooks/use-dialog';
import { Button, Chip, IconButton } from '@hyperflake/react-ui-library';
import { GlobeIcon } from '@icon/index';
import CctvCameraMapPanel from 'modules/cctv-cameras/feature/CctvCameraMapPanel/CctvCameraMapPanel';
import { useCctvCameraListData } from 'modules/cctv-cameras/hooks/useCctvCameraListData';
import useProjectDefaultView from 'modules/projects/hooks/useProjectDefaultView';
import { EmptyDataView, PageHeading, PageTemplate } from 'modules/shared/features/content';
import { BlockLoader } from 'modules/shared/features/loaders';
import useAuthPermission from 'modules/shared/hooks/use-auth-permission';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';
import CctvCameraAddDrawerForm from '../CctvCameraAddDrawerForm/CctvCameraAddDrawerForm';
import CctvCameraCard from '../CctvCameraCard/CctvCameraCard';
import CctvCameraDeleteConfirmationDrawer from '../CctvCameraDeleteConfirmationDrawer/CctvCameraDeleteConfirmationDrawer';
import CctvCameraDrawerLayout from '../CctvCameraDrawerLayout/CctvCameraDrawerLayout';

const CctvCameraList = () => {
    const { clientId, projectId } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = useAuthPermission();

    const editDrawerDialog = useDialog<string>();
    const deleteDrawerDialog = useDialog<ICameraCCTV>();
    const addDrawerDialog = useDialog();

    const { isDefaultView, isMapView, toggleView } = useProjectDefaultView();

    const { cctvCameras, isLoading, addCctvCamera, updateCctvCamera } = useCctvCameraListData(clientId, projectId);

    const renderStatusChip = (status: CameraStatusEnum) => {
        let statusColor: 'primary' | 'warning' | 'success' | 'danger' | 'secondary';
        switch (status) {
            case CameraStatusEnum.ENABLED:
                statusColor = 'success';
                break;
            case CameraStatusEnum.DISBALED:
                statusColor = 'warning';
                break;
            case CameraStatusEnum.DELETED:
                statusColor = 'danger';
                break;
            default:
                statusColor = 'primary';
        }
        return <Chip label={status} variant="outlined" color={statusColor} />;
    };

    if (isLoading) return <BlockLoader />;

    return (
        <PageTemplate>
            <div className="flex justify-between">
                <PageHeading>Live Camera</PageHeading>
                <div className="flex items-center gap-3">
                    {isDefaultView && hasPermission(PermissionEnum.TIMELAPSE_CAMERA_CREATE) && (
                        <Link to={`/clients/${clientId}/projects/${projectId}/cctv-cameras/add-cctv`}>
                            <Button>Add New Live Camera</Button>
                        </Link>
                    )}
                    <IconButton
                        className={twJoin(
                            'relative text-white transition-all duration-200',
                            !isMapView
                                ? 'bg-primary border-primary shadow-lg '
                                : 'bg-primary border-primary hover:shadow-md ring-4 ring-primary/50 animate-pulse'
                        )}
                        onClick={() => toggleView()}
                    >
                        <GlobeIcon />
                    </IconButton>
                </div>
            </div>

            {isDefaultView && (
                <div className="grid grid-cols-12 gap-6 mt-6 empty:hidden">
                    {cctvCameras.map((cctvCamera) => (
                        <Link
                            key={cctvCamera._id}
                            to={`/clients/${clientId}/projects/${projectId}/cctv-cameras/${cctvCamera._id}`}
                            className="col-span-12 sm:col-span-6 lg:col-span-3"
                        >
                            <CctvCameraCard
                                cctvCamera={cctvCamera}
                                clientId={clientId}
                                projectId={projectId}
                                onEditClick={() =>
                                    navigate(
                                        `/clients/${clientId}/projects/${projectId}/cctv-cameras/${cctvCamera._id}/edit-cctv`
                                    )
                                }
                                onLocationClick={() =>
                                    navigate(
                                        `/clients/${clientId}/projects/${projectId}/cctv-cameras/${cctvCamera._id}/edit-cctv`,
                                        { state: { focus: 'map-preview' } }
                                    )
                                }
                                onDeleteClick={(cam) => deleteDrawerDialog.show(cam)}
                                hasDeletePermission={hasPermission(PermissionEnum.LIVE_CAMERA_DELETE)}
                            />
                        </Link>
                    ))}
                </div>
            )}

            {isMapView && (
                <CctvCameraMapPanel
                    cameras={cctvCameras}
                    loadingMessage="Loading Camera Map"
                    onSaveCamera={(cameraId: string, updated: ICameraCCTV) => updateCctvCamera(cameraId, updated)}
                    // onSaveCamera={async (cameraId: string, payload: ICamera) => {
                    //     await cameraListData.updateCamera(cameraId, payload as any);
                    // }}
                />
            )}

            {cctvCameras.length === 0 && <EmptyDataView message="No Live Cameras Found" />}

            {/* Drawers */}
            <CctvCameraAddDrawerForm
                open={addDrawerDialog.isOpen}
                onClose={addDrawerDialog.hide}
                onAddCctvCamera={addCctvCamera}
            />

            <CctvCameraDrawerLayout
                open={editDrawerDialog.isOpen}
                cctvCameraId={editDrawerDialog.data}
                onClose={editDrawerDialog.hide}
                onUpdateCctvCamera={(cam) => updateCctvCamera(cam._id, cam)}
            />

            <CctvCameraDeleteConfirmationDrawer
                open={deleteDrawerDialog.isOpen}
                onClose={deleteDrawerDialog.hide}
                ccCamera={deleteDrawerDialog.data}
            />
        </PageTemplate>
    );
};

export default CctvCameraList;
